import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  doubleFactorial,
  factorial,
  gammaFactorial,
  MAX_FACTORIAL,
  type StirlingResult,
  stirling,
  trailingZeros,
  trailingZeroTerms,
} from "@/lib/tool-kit/arithmetic/factorial";
import { scientificFromDigits } from "@/lib/tool-kit/arithmetic/power";

/**
 * The factorial calculator's results: exact n! and n!!, the digit and trailing-zero
 * counts, Stirling's approximation with its error, and the gamma-function value when n is
 * not a whole number.
 */
export interface FactorialView {
  n: number;
  value: bigint;
  digits: string;
  digitCount: number;
  scientific: { mantissa: string; exponent: number };
  zeros: number;
  zeroTerms: number[];
  double: bigint;
  stirling: StirlingResult | null;
}

export interface GammaView {
  n: number;
  value: number;
}

export type FactorialComputation =
  | { kind: "empty" | "invalid" | "negative" | "too-big" | "gamma-overflow" }
  | { kind: "whole"; view: FactorialView }
  | { kind: "fractional"; view: GammaView };

const SCIENTIFIC_SIG = 10;

export function computeFactorial(raw: string): FactorialComputation {
  if (raw.trim() === "") {
    return { kind: "empty" };
  }
  const n = parseNumberInput(raw);
  if (n === null) {
    return { kind: "invalid" };
  }
  if (Number.isInteger(n)) {
    return wholeNumber(n);
  }
  const value = gammaFactorial(n);
  if (value === null) {
    return { kind: n < 0 ? "negative" : "gamma-overflow" };
  }
  return { kind: "fractional", view: { n, value } };
}

function wholeNumber(n: number): FactorialComputation {
  if (n < 0) {
    return { kind: "negative" };
  }
  if (n > MAX_FACTORIAL) {
    return { kind: "too-big" };
  }
  const value = factorial(n);
  const digits = value.toString();
  const zeroTerms = trailingZeroTerms(n);
  return {
    kind: "whole",
    view: {
      n,
      value,
      digits,
      digitCount: digits.length,
      scientific: scientificFromDigits(digits, SCIENTIFIC_SIG),
      zeros: trailingZeros(n),
      zeroTerms,
      double: doubleFactorial(n),
      stirling: n >= 1 ? stirling(n, value) : null,
    },
  };
}

/* ------------------------------------------------------------- narration */

export interface FactorialWords {
  zeroRule: string;
  chain: string;
  recursion: string;
  trailing: string;
  trailingNone: string;
  doubleLine: string;
  stirlingLine: string;
}

const CHAIN_LIMIT = 15;
const MAX_LIST = 8;

function chainText(n: number): string {
  const parts: string[] = [];
  for (let k = n; k >= 1; k--) {
    parts.push(String(k));
  }
  return parts.join(" × ");
}

function doubleChain(n: number): string {
  const parts: string[] = [];
  for (let k = n; k >= 1; k -= 2) {
    parts.push(String(k));
  }
  return parts.join(" × ");
}

export function factorialLines(
  view: FactorialView,
  words: FactorialWords,
  fmtPercent: (value: number) => string,
  fmtBig: (value: bigint) => string
): string[] {
  const { n } = view;
  const lines: string[] = [];
  if (n === 0) {
    lines.push(words.zeroRule);
    return lines;
  }
  if (n <= CHAIN_LIMIT) {
    lines.push(
      t(words.chain, { n, chain: chainText(n), value: fmtBig(view.value) })
    );
  } else {
    lines.push(
      t(words.recursion, {
        n,
        m: n - 1,
        first: String(n),
        second: String(n - 1),
      })
    );
  }
  if (view.zeroTerms.length === 0) {
    lines.push(t(words.trailingNone, { n }));
  } else {
    const shown = view.zeroTerms.slice(0, MAX_LIST).join(" + ");
    const terms = view.zeroTerms.length > MAX_LIST ? `${shown} + …` : shown;
    lines.push(
      t(words.trailing, {
        n,
        sum: view.zeroTerms.length > 1 ? `${terms} = ${view.zeros}` : terms,
      })
    );
  }
  lines.push(
    t(words.doubleLine, {
      n,
      chain: n <= 30 ? doubleChain(n) : `${n} × ${n - 2} × ${n - 4} × …`,
      value: fmtBig(view.double),
    })
  );
  if (view.stirling) {
    lines.push(
      t(words.stirlingLine, {
        value: `${view.stirling.mantissa} × 10^${view.stirling.exponent}`,
        error: fmtPercent(Math.abs(view.stirling.relativeError) * 100),
      })
    );
  }
  return lines;
}
